import { FC, useState } from "react";

import { AddCourseOptionMenu } from "./AddCourseOptionMenu";
import { showAddCourseModal } from "./AddOrRenameCourseModalUtils";
import { CourseGroupingOptionsMenu } from "./CourseGroupingOptionsMenu";
import { CourseListItem } from "./CourseListItem";
import { SectionDetailTermView } from "./SectionDetailTermView";

import { COURSE_GROUPING_ID_QUERY_PARAM, COURSE_ID_QUERY_PARAM } from "components/constants";
import { EditableText } from "components/hover-widgets/EditableText";
import { t } from "i18n/i18n";
import { Banner } from "mds/components/Banner";
import { FolderIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppDispatch, useAppSelector } from "store/index";
import { courseGroupingSelectors, selectIsEditModeEnabled } from "store/selectors";
import { selectCoursesByGroupingId } from "store/selectors/section";
import { localStateActions } from "store/slices/local";
import { useListSelector, useObjectSelector } from "store/store-hooks";
import { toastSuccessMessage } from "utils/alerts";
import { ADMIN_COURSE_EVENT_PREFIX } from "utils/amplitude";
import { useQueryParam } from "utils/routing";
import { onTitleChanged } from "utils/store-utils";

export const CourseGroupingView: FC = () => {
  const dispatch = useAppDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const { canAuthorOrg } = useCapabilities();
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);
  const canEditGrouping = isEditModeEnabled && canAuthorOrg;
  const groupingId = useQueryParam(COURSE_GROUPING_ID_QUERY_PARAM);
  const currentCourseId = useQueryParam(COURSE_ID_QUERY_PARAM);

  const handleEnableEditMode = () => {
    dispatch(localStateActions.setIsEditMode(true));
  };

  const grouping = useObjectSelector(
    (state) => courseGroupingSelectors.selectById(state, groupingId),
    ["id", "title"],
  );
  const coursesByGrouping = useListSelector(
    (state) => selectCoursesByGroupingId(state, groupingId),
    ["id"],
  );

  // If no grouping_id, show the detail view for the single course
  if (currentCourseId && !grouping) {
    return <SectionDetailTermView canEdit={isEditModeEnabled} courseId={currentCourseId} />;
  }

  if (!grouping) {
    return null;
  }

  return (
    <div className="h-full px-7 py-3">
      <h2 className="h2 my-0 mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FolderIcon className="mb-[3px] text-blue" size="l" />

          <EditableText
            isEditing={isEditing}
            label={t("glossary.course_grouping")}
            textSize="h2"
            value={grouping.title}
            onSetIsEditing={setIsEditing}
            onValueChanged={(newTitle) => {
              onTitleChanged("course_groupings", grouping.id, newTitle);
              toastSuccessMessage(t("home.toasts.grouping_renamed"));
            }}
          />
        </div>

        {canEditGrouping && (
          <div className="flex items-center gap-2">
            <AddCourseOptionMenu groupingId={grouping.id} />

            <CourseGroupingOptionsMenu
              courseGrouping={grouping}
              onRename={() => setIsEditing(true)}
            />
          </div>
        )}
      </h2>

      {coursesByGrouping.length === 0 ? (
        <Banner
          actionButtonText={
            canEditGrouping ? t("common.add.course") : t("warning.enable_edit_mode")
          }
          eventName={
            canEditGrouping
              ? `${ADMIN_COURSE_EVENT_PREFIX}Add Course to Folder`
              : `${ADMIN_COURSE_EVENT_PREFIX}Enable Edit Mode`
          }
          kind="info"
          onAction={canEditGrouping ? () => showAddCourseModal(grouping.id) : handleEnableEditMode}
        >
          {canEditGrouping
            ? t("home.courses.empty_grouping_banner_text")
            : t("home.courses.empty_grouping_banner_text_no_edit")}
        </Banner>
      ) : (
        <div role="list">
          {coursesByGrouping.map((course) => (
            <CourseListItem canEdit={isEditModeEnabled} courseId={course.id} key={course.id} />
          ))}
        </div>
      )}
    </div>
  );
};
